import React, { useEffect, useState } from "react";
import API from "./api";
import { getProfileNumberDisplay, PROFILE_NO_DISPLAY_SORT_KEY } from "./profileNumber";

const PAGE_SIZE = 24;

export default function AssignedProfiles({
  onBack,
  onViewProfile,
  onOpenTasksWorkspace,
  onOpenBiodataWorkspace,
  onSwitchToAssignedByMe,
  canViewProfile = true,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy] = useState("assigned_latest");
  const [viewMode] = useState("grid");

  useEffect(() => {
    loadAssignedProfiles(currentPage, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  async function loadAssignedProfiles(page = 1, sortValue = sortBy) {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        include_meta: "1",
        limit: String(PAGE_SIZE),
        offset: String(Math.max(0, (page - 1) * PAGE_SIZE)),
      });

      if (sortValue === "profile_oldest") {
        params.set("sort_by", PROFILE_NO_DISPLAY_SORT_KEY);
        params.set("sort_dir", "asc");
      } else if (sortValue === "name") {
        params.set("sort_by", "full_name");
        params.set("sort_dir", "asc");
      } else if (sortValue === "age_asc") {
        params.set("sort_by", "age");
        params.set("sort_dir", "asc");
      } else if (sortValue === "age_desc") {
        params.set("sort_by", "age");
        params.set("sort_dir", "desc");
      } else if (sortValue === "profile_latest") {
        params.set("sort_by", PROFILE_NO_DISPLAY_SORT_KEY);
        params.set("sort_dir", "desc");
      } else {
        params.set("sort_by", "assigned_at");
        params.set("sort_dir", "desc");
      }

      const res = await API.get(`/profiles/assigned-to-me?${params.toString()}`);
      const payload = Array.isArray(res.data) ? { items: res.data, total: res.data.length } : (res.data || {});
      const nextItems = safeArray(payload.items);
      const nextTotal = Number(payload.total || 0);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));

      if (page > nextTotalPages) {
        setCurrentPage(nextTotalPages);
        return;
      }

      setItems(nextItems);
      setTotal(nextTotal);
    } catch (err) {
      console.error("Failed to load assigned profiles", err);
      setError(err.response?.data?.error || "Failed to load assigned profiles.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 860px) {
          .jk-assigned-topbar {
            justify-content: center !important;
            flex-direction: column !important;
          }
          .jk-assigned-viewbar {
            justify-content: center !important;
          }
        }
        @media (max-width: 720px) {
          .jk-assigned-results-grid {
            grid-template-columns: 1fr !important;
          }
          .jk-assigned-list-card {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={S.topWrap}>
        <div style={S.topBar} className="jk-assigned-topbar">
          <div style={S.workspaceTabsWrap}>
            <div style={S.workspaceTabs}>
              <button
                type="button"
                style={S.workspaceTabBtn}
                onClick={typeof onOpenTasksWorkspace === "function" ? onOpenTasksWorkspace : undefined}
              >
                Tasks
              </button>
              <button type="button" style={{ ...S.workspaceTabBtn, ...S.workspaceTabBtnActive }}>
                Profile Assignments
              </button>
              <button
                type="button"
                style={S.workspaceTabBtn}
                onClick={typeof onOpenBiodataWorkspace === "function" ? onOpenBiodataWorkspace : undefined}
              >
                Bio Data Assignments
              </button>
            </div>
          </div>

          <div style={S.viewBar} className="jk-assigned-viewbar">
            <span style={S.viewLabel}>VIEW</span>
            <div style={S.viewFilterTabs}>
              <button type="button" style={{ ...S.viewFilterBtn, ...S.viewFilterBtnActive }}>
                Assigned To Me
              </button>
              <button
                type="button"
                style={S.viewFilterBtn}
                onClick={typeof onSwitchToAssignedByMe === "function" ? onSwitchToAssignedByMe : undefined}
              >
                Assigned By Me
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      <div style={S.resultsCard}>
        {loading ? (
          <Splash
            title="Loading assigned profiles"
            subtitle="Pulling your current profile assignments."
          />
        ) : items.length === 0 ? (
          <Splash
            title="No assigned profiles"
            subtitle="There are no active profile allocations on your account right now."
          />
        ) : (
          <>
            <div
              style={viewMode === "grid" ? S.grid : S.list}
              className={viewMode === "grid" ? "jk-assigned-results-grid" : ""}
            >
              {items.map((profile) => (
                <AssignedProfileCard
                  key={profile.id}
                  profile={profile}
                  mode={viewMode}
                  canViewProfile={canViewProfile}
                  onView={() => {
                    if (typeof onViewProfile === "function") {
                      onViewProfile(profile.id);
                    }
                  }}
                />
              ))}
            </div>

            <div style={S.paginationBar}>
              <button
                type="button"
                style={{ ...S.paginationBtn, ...(currentPage <= 1 ? S.paginationBtnDisabled : {}) }}
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                Previous
              </button>

              <span style={S.paginationInfo}>
                Page {currentPage} of {totalPages} | {total} assigned profiles
              </span>

              <button
                type="button"
                style={{ ...S.paginationBtn, ...(currentPage >= totalPages ? S.paginationBtnDisabled : {}) }}
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AssignedProfileCard({ profile, mode, onView, canViewProfile }) {
  const [imgError, setImgError] = useState(false);
  const fullName = getFullName(profile);
  const initials = `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase();
  const avatarBg = pickColor(profile.first_name || "A");
  const age = getAge(profile.birth_date || profile.dob);
  const location = profile.location_display_cache || [profile.city, profile.state].filter(Boolean).join(", ");
  const education = profile.education_summary || "";
  const occupation = profile.occupation_type || "";
  const assignedAt = formatDateTime(profile.assigned_at);
  const assignedBy = formatAssignedBy(profile);
  const showImage = Boolean(profile.photo || profile.primary_photo_url_cache) && !imgError;
  const imageUrl = profile.photo || profile.primary_photo_url_cache;

  if (mode === "list") {
    return (
      <div
        style={S.listCard}
        className="jk-assigned-list-card"
        onClick={canViewProfile ? onView : undefined}
      >
        <div style={S.listMedia}>
          {showImage ? (
            <img src={imageUrl} alt={fullName} style={S.listImage} onError={() => setImgError(true)} />
          ) : (
            <div style={{ ...S.listAvatar, background: avatarBg }}>
              <span style={S.listInitials}>{initials || "?"}</span>
            </div>
          )}
        </div>

        <div style={S.listBody}>
          <div style={S.profileTopRow}>
            <span style={S.profileNo}>#{getProfileNumberDisplay(profile, "-")}</span>
            {assignedAt ? <span style={S.assignedAtBadge}>Assigned {assignedAt}</span> : null}
          </div>
          {assignedBy ? <div style={S.assignmentMeta}>Assigned by {assignedBy}</div> : null}
          <h3 style={S.profileName}>{fullName}</h3>
          <div style={S.metaRow}>
            {age ? <span style={S.metaItem}>{age} yrs</span> : null}
            {profile.gender ? <span style={S.metaItem}>{profile.gender}</span> : null}
            {profile.marital_status ? <span style={S.metaItem}>{profile.marital_status}</span> : null}
          </div>
          {location ? <div style={S.locationText}>{location}</div> : null}
          {(education || occupation) ? (
            <div style={S.infoStack}>
              {education ? <span>{education}</span> : null}
              {occupation ? <span>{occupation}</span> : null}
            </div>
          ) : null}
        </div>

        {canViewProfile ? (
          <button
            type="button"
            style={S.viewBtn}
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            View Profile
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{ ...S.card, ...(canViewProfile ? S.cardClickable : {}) }}
      onClick={canViewProfile ? onView : undefined}
    >
      <div style={S.cardMedia}>
        {showImage ? (
          <img src={imageUrl} alt={fullName} style={S.cardImage} onError={() => setImgError(true)} />
        ) : (
          <div style={{ ...S.cardAvatar, background: avatarBg }}>
            <span style={S.cardInitials}>{initials || "?"}</span>
          </div>
        )}
        <div style={S.cardOverlay}>
          <span style={S.profileNo}>#{getProfileNumberDisplay(profile, "-")}</span>
          <div style={S.overlayMetaStack}>
            {assignedAt ? <span style={S.assignedAtBadge}>Assigned {assignedAt}</span> : null}
            {assignedBy ? <span style={S.assignedByBadge}>By {assignedBy}</span> : null}
          </div>
        </div>
      </div>

      <div style={S.cardBody}>
        <h3 style={S.profileName}>{fullName}</h3>
        <div style={S.metaRow}>
          {age ? <span style={S.metaItem}>{age} yrs</span> : null}
          {profile.gender ? <span style={S.metaItem}>{profile.gender}</span> : null}
          {profile.marital_status ? <span style={S.metaItem}>{profile.marital_status}</span> : null}
        </div>
        {location ? <div style={S.locationText}>{location}</div> : null}
        {(education || occupation) ? (
          <div style={S.infoStack}>
            {education ? <span>{education}</span> : null}
            {occupation ? <span>{occupation}</span> : null}
          </div>
        ) : null}
        {canViewProfile ? (
          <button
            type="button"
            style={S.viewBtn}
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            View Profile
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Splash({ title, subtitle }) {
  return (
    <div style={S.splash}>
      <div style={S.splashOrb} />
      <p style={S.splashTitle}>{title}</p>
      <p style={S.splashSub}>{subtitle}</p>
    </div>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function getFullName(profile) {
  return profile.full_name || [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ") || "Unnamed Profile";
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const dob = new Date(dateValue);
  if (Number.isNaN(dob.getTime())) return null;
  return Math.floor((new Date() - dob) / (365.25 * 86400000));
}

function pickColor(str) {
  const palette = ["#D73C2E", "#B9343A", "#C7433F", "#A93338", "#D1483A", "#252935"];
  let hash = 0;
  for (const char of str) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatAssignedBy(profile) {
  if (profile?.assigned_by_email) {
    return String(profile.assigned_by_email).split("@")[0];
  }
  if (profile?.assigned_by_user_id) {
    return `user #${profile.assigned_by_user_id}`;
  }
  return "";
}

const C = {
  bg: "#fff9f7",
  card: "#ffffff",
  border: "rgba(185,52,58,0.12)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  text: "#252935",
  muted: "#6b6f7d",
  shadow: "0 22px 55px rgba(215,60,46,0.10)",
};

const S = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  topWrap: {
    display: "flex",
    justifyContent: "center",
  },
  topBar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  workspaceTabsWrap: {
    display: "flex",
    justifyContent: "center",
    width: "auto",
  },
  workspaceTabs: {
    display: "inline-flex",
    justifyContent: "center",
    gap: 10,
    padding: 8,
    borderRadius: 18,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    boxShadow: "0 12px 30px rgba(185,52,58,0.08)",
    flexWrap: "wrap",
  },
  workspaceTabBtn: {
    border: "none",
    background: "#fff7f4",
    color: C.muted,
    borderRadius: 12,
    padding: "12px 18px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    outline: "none",
    boxShadow: "none",
  },
  workspaceTabBtnActive: {
    background: C.accent,
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(215,60,46,0.18)",
  },
  viewBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  viewLabel: {
    color: "#8b4c2e",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 1.8,
    textTransform: "uppercase",
  },
  viewFilterTabs: {
    display: "inline-flex",
    gap: 8,
    padding: 4,
    borderRadius: 16,
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(185,52,58,0.10)",
  },
  viewFilterBtn: {
    border: "none",
    background: "transparent",
    color: "#6b6f7d",
    borderRadius: 12,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    outline: "none",
    boxShadow: "none",
  },
  viewFilterBtnActive: {
    background: "#ffffff",
    color: "#252935",
    boxShadow: "0 8px 18px rgba(37,41,53,0.08)",
  },
  title: {
    margin: "10px 0 8px",
    fontSize: 46,
    lineHeight: 1,
    color: C.text,
  },
  subtitle: {
    margin: 0,
    color: C.muted,
    fontSize: 18,
    lineHeight: 1.55,
    maxWidth: 680,
  },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  backBtn: {
    border: `1px solid ${C.border}`,
    background: "#ffffff",
    color: C.text,
    borderRadius: 999,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  toolbarCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "14px 18px",
    borderRadius: 22,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
  },
  resultsCard: {
    background: "#ffffff",
    borderRadius: 28,
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  toolbarGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  toolbarLabel: {
    fontSize: 13,
    fontWeight: 800,
    color: C.muted,
  },
  toolbarSelect: {
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
    background: "#ffffff",
  },
  toolbarDivider: {
    width: 1,
    height: 34,
    background: C.border,
  },
  viewToggle: {
    display: "inline-flex",
    padding: 4,
    borderRadius: 999,
    background: C.accentLight,
    gap: 4,
  },
  viewToggleBtn: {
    border: "none",
    background: "transparent",
    color: C.muted,
    padding: "9px 14px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  viewToggleActive: {
    background: C.accent,
    color: "#ffffff",
  },
  errorBox: {
    padding: "14px 16px",
    borderRadius: 20,
    background: "#fff4f1",
    border: `1px solid ${C.border}`,
    color: C.accentDark,
    fontWeight: 700,
  },
  splash: {
    position: "relative",
    overflow: "hidden",
    minHeight: 240,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 10,
    background: "transparent",
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    padding: 28,
  },
  splashOrb: {
    position: "absolute",
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(215,60,46,0.12) 0%, rgba(215,60,46,0) 72%)",
    top: -70,
    right: -40,
  },
  splashTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 900,
    color: C.text,
  },
  splashSub: {
    margin: 0,
    maxWidth: 520,
    color: C.muted,
    fontSize: 15,
    lineHeight: 1.6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 18,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  card: {
    background: "#ffffff",
    borderRadius: 28,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
  },
  cardClickable: {
    cursor: "pointer",
  },
  cardMedia: {
    position: "relative",
    height: 250,
    background: "#252935",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  cardAvatar: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardInitials: {
    color: "#ffffff",
    fontSize: 72,
    fontWeight: 900,
    letterSpacing: "0.04em",
  },
  cardOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 14,
    background: "linear-gradient(180deg, rgba(12,14,20,0.14) 0%, rgba(12,14,20,0.02) 38%, rgba(12,14,20,0.16) 100%)",
  },
  overlayMetaStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 8,
  },
  profileNo: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "rgba(215,60,46,0.95)",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 900,
  },
  assignedAtBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "rgba(255,255,255,0.94)",
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
  },
  assignedByBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "6px 10px",
    background: "rgba(37,41,53,0.82)",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 800,
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 20,
  },
  profileName: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.15,
    color: C.text,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  metaItem: {
    color: C.muted,
    fontSize: 14,
    fontWeight: 800,
  },
  assignmentMeta: {
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 800,
  },
  locationText: {
    color: C.text,
    fontSize: 14,
    fontWeight: 700,
  },
  infoStack: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.55,
  },
  viewBtn: {
    alignSelf: "flex-start",
    border: "none",
    background: C.accent,
    color: "#ffffff",
    borderRadius: 999,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  listCard: {
    display: "grid",
    gridTemplateColumns: "170px minmax(0, 1fr) auto",
    gap: 18,
    alignItems: "center",
    background: "#ffffff",
    borderRadius: 24,
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
    padding: 16,
  },
  listMedia: {
    height: 140,
    borderRadius: 20,
    overflow: "hidden",
    background: "#252935",
  },
  listImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  listAvatar: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  listInitials: {
    color: "#ffffff",
    fontSize: 46,
    fontWeight: 900,
  },
  listBody: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },
  profileTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  paginationBar: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 18px",
    borderRadius: 22,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
  },
  paginationBtn: {
    border: "none",
    background: C.accent,
    color: "#ffffff",
    borderRadius: 999,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  paginationBtnDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  paginationInfo: {
    color: C.muted,
    fontSize: 14,
    fontWeight: 800,
    textAlign: "center",
  },
};
